import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Coffee, Phone, Mail, MapPin, Clock, Star, Calendar, Users, 
  Truck, Heart, ChevronRight, Instagram, Facebook, Sparkles,
  IceCream, Leaf, UtensilsCrossed, Settings, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category icons
const CATEGORY_ICONS = {
  coffee: Coffee,
  tea: Leaf,
  milkshakes: IceCream,
  smoothies: Sparkles,
  snowcones: IceCream,
  food: UtensilsCrossed,
};

// Images
const VAN_IMAGES = {
  hero: 'https://images.pexels.com/photos/5920735/pexels-photo-5920735.jpeg?auto=compress&cs=tinysrgb&w=1200',
  van1: 'https://images.pexels.com/photos/5920633/pexels-photo-5920633.jpeg?auto=compress&cs=tinysrgb&w=800',
  van2: 'https://images.unsplash.com/photo-1762787804827-a7a92af648bc?w=800',
};

const CoffeeOnTheGo = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [activeCategory, setActiveCategory] = useState('coffee');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const [menuRes, specialsRes] = await Promise.all([
        fetch(`${API_URL}/api/coffeeonthego/menu`),
        fetch(`${API_URL}/api/coffeeonthego/specials`)
      ]);
      
      const menuData = await menuRes.json();
      const specialsData = await specialsRes.json();
      
      setMenuItems(menuData.items || []);
      setSpecials(specialsData.specials || []);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      // Use default menu if API fails
      setMenuItems(DEFAULT_MENU);
      setSpecials(DEFAULT_SPECIALS);
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(menuItems.map(item => item.category))];
  const filteredItems = menuItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-orange-50">
      <Helmet>
        <title>Coffee On The Go | Mobile Coffee Van - Port Macquarie</title>
        <meta name="description" content="We get coffee right first time every time. Premium mobile coffee van serving Port Macquarie, Mid North Coast markets, events and functions." />
      </Helmet>

      {/* Header */}
      <header className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Coffee className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-xl font-black">COFFEE ON THE GO</h1>
                <p className="text-amber-200 text-xs">We get coffee right first time every time</p>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <a href="#about" className="hover:text-amber-200 transition">About</a>
              <a href="#menu" className="hover:text-amber-200 transition">Menu</a>
              <a href="#locations" className="hover:text-amber-200 transition">Locations</a>
              <a href="#events" className="hover:text-amber-200 transition">Events</a>
              <a href="#contact" className="hover:text-amber-200 transition">Contact</a>
            </nav>
            
            <a 
              href="tel:0431775771" 
              className="flex items-center gap-2 bg-white text-amber-900 px-4 py-2 rounded-full font-bold hover:bg-amber-100 transition"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">0431 775 771</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={VAN_IMAGES.hero} 
            alt="Coffee On The Go Van" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 via-amber-900/80 to-amber-900/60"></div>
        </div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Truck className="w-5 h-5" />
                <span className="text-sm font-medium">Mobile Coffee Van • Mid North Coast</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                COFFEE
                <span className="block text-amber-300">ON THE GO</span>
              </h2>
              
              <p className="text-xl md:text-2xl text-amber-100 mb-8 font-light">
                We get coffee right<br/>
                <span className="font-bold">first time every time</span>
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a 
                  href="#menu"
                  className="inline-flex items-center gap-2 bg-white text-amber-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-amber-100 transition shadow-xl"
                >
                  <Coffee className="w-5 h-5" />
                  View Menu
                </a>
                <a 
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-amber-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-amber-500 transition shadow-xl"
                >
                  <Calendar className="w-5 h-5" />
                  Book Our Van
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-400 to-orange-400 rounded-3xl blur-2xl opacity-30"></div>
              <div className="relative rounded-3xl overflow-hidden border border-white/20 shadow-2xl">
                <img 
                  src={VAN_IMAGES.van1} 
                  alt="Coffee On The Go Van serving customers" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <div className="text-center">
                    <h3 className="text-white text-xl font-bold mb-1">Premium Coffee</h3>
                    <p className="text-amber-200 text-sm">Proudly serving Australian specialty roasters</p>
                    
                    <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-black text-amber-300">15+</div>
                        <div className="text-xs text-amber-200">Years</div>
                      </div>
                      <div>
                        <div className="text-2xl font-black text-amber-300">5</div>
                        <div className="text-xs text-amber-200">Locations</div>
                      </div>
                      <div>
                        <div className="text-2xl font-black text-amber-300">1000s</div>
                        <div className="text-xs text-amber-200">Customers</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specials Banner */}
      {specials.length > 0 && (
        <section className="bg-gradient-to-r from-red-500 to-orange-500 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-4 text-white">
              <Sparkles className="w-6 h-6 animate-pulse" />
              <span className="font-bold text-lg">TODAY'S SPECIALS:</span>
              {specials.map((special, idx) => (
                <span key={idx} className="bg-white/20 px-4 py-1 rounded-full">
                  {special.name} - <span className="font-bold">${special.price}</span>
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Menu Section */}
      <section id="menu" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-amber-900 mb-4">Our Menu</h2>
            <p className="text-xl text-amber-700">Crafted with passion, served with love</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {['coffee', 'tea', 'snowcones', 'milkshakes', 'smoothies', 'food'].map((cat) => {
              const Icon = CATEGORY_ICONS[cat] || Coffee;
              const hasItems = menuItems.some(item => item.category === cat);
              if (!hasItems) return null;
              
              const label = cat === 'snowcones' ? 'Snow Cones' : cat.charAt(0).toUpperCase() + cat.slice(1);
              
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                    activeCategory === cat
                      ? cat === 'snowcones' ? 'bg-cyan-500 text-white shadow-lg' : 'bg-amber-900 text-white shadow-lg'
                      : 'bg-white text-amber-800 hover:bg-amber-100 border border-amber-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                  {cat === 'snowcones' && <span className="ml-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">HOT!</span>}
                </button>
              );
            })}
          </div>

          {/* Menu Items Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-amber-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-amber-900">{item.name}</h3>
                    {item.description && (
                      <p className="text-amber-600 text-sm mt-1">{item.description}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {item.sizes ? (
                      <div className="space-y-1">
                        {item.sizes.map((size, sIdx) => (
                          <div key={sIdx} className="text-sm">
                            <span className="text-amber-600">{size.name}</span>
                            <span className="ml-2 font-bold text-amber-900">${size.price}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-2xl font-black text-amber-900">${item.price}</span>
                    )}
                  </div>
                </div>
                {item.tags && (
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag, tIdx) => (
                      <span key={tIdx} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && !loading && (
            <div className="text-center py-12 text-amber-600">
              <Coffee className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Menu items coming soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 rounded-full px-4 py-2 mb-6">
                <Heart className="w-5 h-5" />
                <span className="font-medium">Our Story</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-black text-amber-900 mb-6">
                About Coffee On The Go
              </h2>
              
              <div className="space-y-4 text-amber-800 text-lg leading-relaxed">
                <p>
                  <strong>Coffee On The Go</strong> has been serving the Mid North Coast community 
                  for over 15 years. What started as a passion for great coffee has grown into a 
                  beloved local institution.
                </p>
                <p>
                  Run by <strong>Big Pauly</strong>, our mobile coffee van brings premium espresso, 
                  refreshing snow cones, and delicious treats to markets, sporting events, and 
                  beautiful Pilot Beach.
                </p>
                <p>
                  We believe in getting your coffee <strong>right first time, every time</strong>. 
                  That's not just our tagline – it's our promise to every customer.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black text-amber-600">15+</div>
                  <div className="text-sm text-amber-700">Years Serving</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-amber-600">1000s</div>
                  <div className="text-sm text-amber-700">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-amber-600">5★</div>
                  <div className="text-sm text-amber-700">Service Rating</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-amber-300 to-orange-300 rounded-3xl blur-2xl opacity-40"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coffee className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-amber-900">Our Promise</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Star className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-amber-900">Quality First</h4>
                      <p className="text-amber-700 text-sm">Only the finest Australian beans and fresh ingredients</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Star className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-amber-900">Friendly Service</h4>
                      <p className="text-amber-700 text-sm">A smile with every cup – we love our community</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Star className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-amber-900">Consistency</h4>
                      <p className="text-amber-700 text-sm">Right first time, every time – that's our guarantee</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-20 bg-amber-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4">Where to Find Us</h2>
            <p className="text-xl text-amber-200">Catch us at these regular locations</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LOCATIONS.map((location, idx) => (
              <div 
                key={idx}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">{location.name}</h3>
                    <p className="text-amber-200 text-sm mb-2">{location.address}</p>
                    <div className="flex items-center gap-2 text-amber-300 text-sm">
                      <Clock className="w-4 h-4" />
                      {location.when}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events & Hire Section */}
      <section id="events" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-amber-900 mb-6">
                Hire Our Coffee Van
              </h2>
              <p className="text-xl text-amber-700 mb-8">
                Make your event unforgettable with Coffee On The Go! We bring premium coffee 
                experiences to your special occasions.
              </p>
              
              <div className="space-y-4 mb-8">
                {['Weddings & Engagements', 'Corporate Events', 'Sports Days & Carnivals', 'Birthday Parties', 'Community Markets', 'Private Functions'].map((event, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Heart className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-amber-800 font-medium">{event}</span>
                  </div>
                ))}
              </div>
              
              <a 
                href="tel:0431775771"
                className="inline-flex items-center gap-2 bg-amber-900 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-amber-800 transition"
              >
                <Phone className="w-5 h-5" />
                Call to Book: 0431 775 771
              </a>
            </div>
            
            <div className="relative rounded-3xl overflow-hidden">
              <img 
                src={VAN_IMAGES.van2} 
                alt="Coffee van at event" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-amber-900/90 via-amber-900/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="text-center text-white">
                  <div className="w-20 h-20 bg-amber-900/80 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Who We Serve</h3>
                  <div className="space-y-2 text-amber-100">
                    <p>Group 3 NRL Football Games</p>
                    <p>Port Macquarie Soccer Home Games</p>
                    <p>Local Markets & Community Events</p>
                    <p>Pilot Beach (Summer Season)</p>
                    <p>Private & Corporate Functions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-amber-900 mb-4">What People Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-amber-800 mb-4 italic">"{testimonial.text}"</p>
                <div className="font-bold text-amber-900">{testimonial.name}</div>
                <div className="text-sm text-amber-600">{testimonial.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-amber-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-black mb-6">Get In Touch</h2>
              <p className="text-xl text-amber-200 mb-8">
                Ready to book our van or have questions? We'd love to hear from you!
              </p>
              
              <div className="space-y-6">
                <a href="tel:0431775771" className="flex items-center gap-4 text-xl hover:text-amber-300 transition">
                  <div className="w-14 h-14 bg-amber-800 rounded-xl flex items-center justify-center">
                    <Phone className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-amber-300 text-sm">Call Us</div>
                    <div className="font-bold">0431 775 771</div>
                  </div>
                </a>
                
                <a href="mailto:hello@coffeeonthego.com.au" className="flex items-center gap-4 text-xl hover:text-amber-300 transition">
                  <div className="w-14 h-14 bg-amber-800 rounded-xl flex items-center justify-center">
                    <Mail className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-amber-300 text-sm">Email Us</div>
                    <div className="font-bold">hello@coffeeonthego.com.au</div>
                  </div>
                </a>
                
                <div className="flex items-center gap-4 text-xl">
                  <div className="w-14 h-14 bg-amber-800 rounded-xl flex items-center justify-center">
                    <MapPin className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-amber-300 text-sm">Serving</div>
                    <div className="font-bold">Port Macquarie & Mid North Coast</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6">Send a Message</h3>
              <form className="space-y-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <textarea
                  placeholder="Your Message..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                ></textarea>
                <Button className="w-full bg-white text-amber-900 hover:bg-amber-100 font-bold py-3">
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-950 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-800 rounded-full flex items-center justify-center">
                <Coffee className="w-6 h-6" />
              </div>
              <div>
                <div className="font-black text-lg">COFFEE ON THE GO</div>
                <div className="text-amber-400 text-sm">We get coffee right first time every time</div>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-amber-400 text-sm mb-2">A Lionheart Digital Asset</p>
              <p className="text-amber-600 text-xs">&copy; {new Date().getFullYear()} Coffee On The Go. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Link - small & subtle */}
      <Link 
        to="/coffeeonthego/admin" 
        className="fixed bottom-4 right-4 p-2 bg-amber-900/50 hover:bg-amber-900 text-white rounded-full opacity-30 hover:opacity-100 transition-opacity"
        title="Admin"
      >
        <Settings className="w-5 h-5" />
      </Link>
    </div>
  );
};

// Default data
const DEFAULT_MENU = [
  { category: 'coffee', name: 'Espresso', description: 'Rich & bold single shot', sizes: [{ name: 'Single', price: '4.00' }, { name: 'Double', price: '5.00' }] },
  { category: 'coffee', name: 'Flat White', description: 'Smooth & silky', sizes: [{ name: 'Small', price: '5.00' }, { name: 'Medium', price: '6.00' }, { name: 'Large', price: '7.00' }] },
  { category: 'coffee', name: 'Cappuccino', description: 'Classic with frothy milk', sizes: [{ name: 'Small', price: '5.00' }, { name: 'Medium', price: '6.00' }, { name: 'Large', price: '7.00' }] },
  { category: 'coffee', name: 'Latte', description: 'Smooth coffee with steamed milk', sizes: [{ name: 'Small', price: '5.00' }, { name: 'Medium', price: '6.00' }, { name: 'Large', price: '7.00' }] },
  { category: 'coffee', name: 'Mocha', description: 'Coffee meets chocolate', sizes: [{ name: 'Small', price: '5.50' }, { name: 'Medium', price: '6.50' }, { name: 'Large', price: '7.50' }] },
  { category: 'coffee', name: 'Long Black', description: 'Strong & smooth', sizes: [{ name: 'Small', price: '5.00' }, { name: 'Medium', price: '6.00' }, { name: 'Large', price: '7.00' }] },
  { category: 'tea', name: 'English Breakfast', price: '4.50' },
  { category: 'tea', name: 'Earl Grey', price: '4.50' },
  { category: 'tea', name: 'Green Tea', price: '4.50' },
  { category: 'tea', name: 'Chai Latte', description: 'Spiced & creamy', sizes: [{ name: 'Small', price: '5.50' }, { name: 'Large', price: '6.50' }] },
  { category: 'milkshakes', name: 'Chocolate Milkshake', price: '7.00' },
  { category: 'milkshakes', name: 'Strawberry Milkshake', price: '7.00' },
  { category: 'milkshakes', name: 'Vanilla Milkshake', price: '7.00' },
  { category: 'milkshakes', name: 'Caramel Milkshake', price: '7.00' },
  { category: 'smoothies', name: 'Mango Smoothie', description: 'Fresh & tropical', price: '8.00' },
  { category: 'smoothies', name: 'Berry Blast', description: 'Mixed berries', price: '8.00' },
  { category: 'smoothies', name: 'Banana Smoothie', price: '8.00' },
  { category: 'food', name: 'Toasted Sandwich', description: 'Ham & cheese', price: '8.00' },
  { category: 'food', name: 'Muffin', description: 'Assorted flavours', price: '5.00' },
  { category: 'food', name: 'Banana Bread', description: 'Fresh baked', price: '5.50' },
];

const DEFAULT_SPECIALS = [];

const LOCATIONS = [
  { name: 'Laurieton Riverwalk Markets', address: 'Bruce Porter Reserve, Laurieton', when: '3rd Sunday of every month' },
  { name: 'Port Macquarie Markets', address: 'Port Macquarie', when: '2nd Saturday of every month' },
  { name: 'Port Regional Stadium', address: 'Port Macquarie', when: 'Group 3 NRL Game Days' },
  { name: 'Rainbow Beach Soccer Fields', address: 'Port Macquarie', when: 'Soccer Home Games' },
  { name: 'Pilot Beach', address: 'Camden Head Road', when: 'December - February (Summer)' },
];

const TESTIMONIALS = [
  { name: 'Mary G.', role: 'Regular Customer', text: 'Best coffee on the Mid North Coast! The team are incredibly friendly and always remember my order.' },
  { name: 'Tracey B.', role: 'Coffee Fan', text: 'The staff are incredibly friendly, attentive, and always happy to share their knowledge about coffee. Top-notch service!' },
  { name: 'Local Soccer Club', role: 'Event Hire', text: 'Coffee On The Go makes our home games so much better. The players and supporters love them!' },
];

export default CoffeeOnTheGo;
